import { useEffect } from 'react';

const BANNER_SELECTOR = '[data-testid="styled-banner"]';

/**
 * Observes the Intercom banner and sets a CSS variable for layout adjustment.
 * When the banner appears/disappears, updates --intercom-banner-height and
 * dispatches a resize event to trigger DataGrid recalculation.
 */
export function useIntercomBannerObserver(): void {
  useEffect(() => {
    let resizeObserver: ResizeObserver | null = null;

    const updateBannerHeight = () => {
      const banner = document.querySelector(
        BANNER_SELECTOR
      ) as HTMLElement | null;
      const height = banner?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        '--intercom-banner-height',
        `${height}px`
      );
      window.dispatchEvent(new Event('resize'));
    };

    const observeBanner = () => {
      const banner = document.querySelector(BANNER_SELECTOR);
      if (banner && !resizeObserver) {
        resizeObserver = new ResizeObserver(updateBannerHeight);
        resizeObserver.observe(banner);
      } else if (!banner && resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      updateBannerHeight();
    };

    const mutationObserver = new MutationObserver(observeBanner);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    observeBanner();

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      document.documentElement.style.setProperty(
        '--intercom-banner-height',
        '0px'
      );
    };
  }, []);
}
